import { useEffect } from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';

import { usePathname } from 'src/routes/hooks';

import { useResponsive } from 'src/hooks/use-responsive';

import { useTranslate } from 'src/locales';
import { useDomainContext } from 'src/contexts/domain-context';

import { useSettingsContext } from 'src/components/settings';

import { NAV, HEADER } from '../config-layout';

// ----------------------------------------------------------------------

const SPACING = 8;

const useUpdateDomainInfo = () => {
  const { domainData } = useDomainContext();
  const { onChangeLang, i18n } = useTranslate();

  useEffect(() => {
    const langStorage = localStorage.getItem('i18nextLng');
    if (langStorage) {
      if (langStorage !== i18n.language) {
        onChangeLang(langStorage);
      }
    } else if (domainData?.internal_brand?.default_lng) {
      if (domainData.internal_brand.default_lng !== i18n.language) {
        onChangeLang(domainData.internal_brand.default_lng);
      }
    }
    if (domainData) {
      localStorage.setItem('domain_data', JSON.stringify(domainData));
    }
  }, [domainData, i18n.language]);
};

export default function Main({ children, sx, ...other }) {
  const settings = useSettingsContext();
  const pathname = usePathname();

  const lgUp = useResponsive('up', 'lg');

  const isNavHorizontal = settings.themeLayout === 'horizontal';

  const isNavMini = settings.themeLayout === 'mini';

  useUpdateDomainInfo();

  if (isNavHorizontal) {
    return (
      <Box
        component="main"
        sx={{
          minHeight: 1,
          display: 'flex',
          flexDirection: 'column',
          pt: `${HEADER.H_MOBILE + 24}px`,
          pb: 10,
          ...(lgUp && {
            pt: `${HEADER.H_MOBILE * 2 + 40}px`,
            pb: 15,
          }),
        }}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        minHeight: 1,
        display: 'flex',
        flexDirection: 'column',
        py: `${HEADER.H_MOBILE + SPACING}px`,
        pb: pathname === '/dashboard' ? 2 : 'auto',
        ...(lgUp && {
          px: 2,
          py: `${HEADER.H_DESKTOP + SPACING}px`,
          pb: pathname === '/dashboard' ? 2 : 'auto',
          width: `calc(100% - ${NAV.W_VERTICAL}px)`,
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_MINI}px)`,
          }),
        }),
        ...sx,
      }}
      {...other}
    >
      {children}
    </Box>
  );
}

Main.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
};
